import { type AIAgentExecutionPayload, type ExecuteAIAgentRequest } from '../../../store/ai/aiAgent'
import { type AIAgentExecutionResult } from '../../../models/AIAgent'
import { type AccountOpeningPrefill } from '../../../models/AccountOpeningDescriptor'
import { type FieldCitations } from '../../../models/Citation'
import { asFieldCitations } from './citations'
import { partySchemaRole } from './parties'
import { isRecord, withoutBlanks, type Values } from './schemaValues'
import { type WizardAccount, type WizardContext, type WizardParty } from './types'

export const accountPrefillSources = (ctx: WizardContext, account: WizardAccount, schema: Record<string, unknown>): Record<string, unknown> => ({
  schema,
  accountTypeId: account.typeId,
  roomId: ctx.roomId,
  sessionId: ctx.sessionId,
  meetingTemplateId: ctx.meetingTemplateId,
  contactId: ctx.contactId,
  includeCitations: true,
})

export const partyPrefillSources = (
  ctx: WizardContext,
  party: WizardParty,
  schema: Record<string, unknown>,
  clientContactId: string | null,
): Record<string, unknown> => ({
  schema,
  role: partySchemaRole(party),
  who: party.name,
  roomId: ctx.roomId,
  sessionId: ctx.sessionId,
  meetingTemplateId: ctx.meetingTemplateId,
  contactId: party.contactId,
  clientContactId: party.contactId ? null : (clientContactId ?? ctx.contactId),
  includeCitations: true,
})

const buildPrefillContext = (keys: string[], sources: Record<string, unknown>): Record<string, unknown> => {
  const context: Record<string, unknown> = {}
  keys.forEach((key) => {
    const value = sources[key]
    if (value === null || value === undefined || value === '') {
      return
    }
    context[key] = value
  })
  return context
}

export const prefillHasSource = (prefill: AccountOpeningPrefill, sources: Record<string, unknown>): boolean => {
  const context = buildPrefillContext(prefill.context, sources)
  return !prefill.requiresAnyOf || prefill.requiresAnyOf.some((keys) => keys.every((key) => key in context))
}

const prefillPayload = (prefill: AccountOpeningPrefill, sources: Record<string, unknown>): AIAgentExecutionPayload => ({
  type: prefill.agent,
  context: buildPrefillContext(prefill.context, sources),
})

export interface PrefillTarget {
  key: string
  sources: Record<string, unknown>
}

export interface PrefillOutcome {
  values: Values
  citations: FieldCitations
}

export type PrefillResults = Map<string, PrefillOutcome>

// With `includeCitations` the agent answers `{values, citations}`; an agent that does not know the flag still answers the bare values.
export const parsePrefillResult = (result: unknown): PrefillOutcome => {
  if (isRecord(result) && isRecord(result.values) && isRecord(result.citations)) {
    return { values: withoutBlanks(result.values), citations: asFieldCitations(result.citations) }
  }
  return { values: withoutBlanks(isRecord(result) ? result : {}), citations: {} }
}

export const fetchPrefilled = async ({
  executePrefill,
  accountId,
  prefill,
  targets,
  suppressGlobalErrorToast = false,
}: {
  executePrefill: (args: ExecuteAIAgentRequest) => { unwrap: () => Promise<unknown> }
  accountId: string
  prefill: AccountOpeningPrefill
  targets: PrefillTarget[]
  suppressGlobalErrorToast?: boolean
}): Promise<{ filled: PrefillResults; failedKeys: string[] }> => {
  const filled: PrefillResults = new Map()
  const failedKeys: string[] = []
  await Promise.all(
    targets.map(async (target) => {
      try {
        const response = (await executePrefill({
          accountId,
          payload: prefillPayload(prefill, target.sources),
          suppressGlobalErrorToast,
        }).unwrap()) as AIAgentExecutionResult<Record<string, unknown>>
        filled.set(target.key, parsePrefillResult(response.result))
      } catch {
        failedKeys.push(target.key)
      }
    }),
  )
  return { filled, failedKeys }
}
